import React from 'react'
import { CLUSTER_INSET, CONTROL_BUTTON_SIZE } from './layout'

const styles: { [key: string]: React.CSSProperties } = {
  playerWrapper: {
    position: 'relative',
    width: '100%',
  },
  canvasContainer: {
    width: '100%',
    position: 'relative',
    aspectRatio: '4 / 3',
    minHeight: '300px',  // Fallback for browsers that don't support aspectRatio
    margin: '0 0 5px 0',
    backgroundColor: '#000',
    overflow: 'hidden',
    cursor: 'grab',
  },
  canvasContainerCompact: {
    width: '100%',
    position: 'relative',
    aspectRatio: '4 / 3',
    minHeight: '200px',
    margin: '0 0 5px 0',
    backgroundColor: '#000',
    overflow: 'hidden',
    cursor: 'grab',
  },
  // Top-right cluster: compass stacked above the camera controls. Stacking
  // (rather than a fixed offset) keeps the buttons below the compass even as
  // it scales up in fullscreen.
  topRightCluster: {
    position: 'absolute',
    top: `${CLUSTER_INSET}px`,
    right: `${CLUSTER_INSET}px`,
    zIndex: 5,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '6px',
  },
  controlsOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 10,
    display: 'flex',
    flexDirection: 'column',
    padding: '0 8px 4px 8px',
    background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.3) 80%, rgba(0,0,0,0) 100%)',
  },
  // The scrub row: elapsed time, the bar, total time — the times flank the
  // bar as the BBC does, rather than sitting in the button row.
  scrubRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    marginBottom: '6px',
  },
  // Grows to fill whatever the two time labels leave. `position: relative` is
  // load-bearing: the hover readout is absolutely positioned against this box,
  // so the pointer offset it is given is measured in the same coordinates.
  progressBarContainer: {
    position: 'relative',
    flex: '1 1 auto',
    minWidth: 0,
    height: '6px',
    cursor: 'pointer',
  },
  // `display: block` and no margin are load-bearing. An input is inline by
  // default, so it sat on the text baseline of a line box taller than this 6px
  // container and hung below it — the bar drew several px under the centre
  // line the time labels are aligned to, and the times looked raised.
  progressInput: {
    display: 'block',
    margin: 0,
    width: '100%',
    height: '100%',
    appearance: 'none',
    background: 'rgba(255,255,255,0.3)',
    borderRadius: '3px',
    accentColor: '#FF0000',
    cursor: 'pointer',
  },
  // Height follows the button (4px of breathing room above and below), so
  // growing CONTROL_BUTTON_SIZE grows the bar instead of overflowing it.
  controlsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    width: '100%',
    height: `${CONTROL_BUTTON_SIZE + 8}px`,
  },
  // The three zones of the BBC skeleton. Left and right share an identical
  // `flex: 1 1 0`, so they always resolve to the same width whatever they
  // hold — that, and only that, is what keeps the centre cluster on the
  // player's true midline as the right cluster gains and loses the
  // verification glyph and the 360 button between videos.
  controlZoneLeft: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '6px',
    flex: '1 1 0',
    minWidth: 0,
  },
  controlZoneCentre: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    flex: '0 0 auto',
  },
  controlZoneRight: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '6px',
    flex: '1 1 0',
    minWidth: 0,
  },
  // Narrow video columns (< NARROW_CONTROLS_WIDTH, re-measured at 600px in
  // T10) drop the volume slider and tighten the row gap from 10px to 6px, so
  // the left zone still holds its two buttons.
  controlsRowNarrow: {
    gap: '6px',
  },
  button: {
    backgroundColor: 'transparent',
    color: 'white',
    border: 'none',
    padding: '0',
    cursor: 'pointer',
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: `${CONTROL_BUTTON_SIZE}px`,
    minHeight: `${CONTROL_BUTTON_SIZE}px`,
  },
  volumeContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  volumeSlider: {
    width: '70px',
    height: '5px',
    margin: 0,
    appearance: 'none',
    borderRadius: '3px',
    cursor: 'pointer',
    accentColor: '#fff',
  },
  // One end of the scrub row. Monospace and a minWidth so the bar does not
  // twitch sideways as the digits change (0:59 -> 1:00) or the hour rolls in.
  timeLabel: {
    fontFamily: 'monospace',
    color: '#fff',
    fontSize: '13px',
    minWidth: '44px',
    textAlign: 'center',
    whiteSpace: 'nowrap' as const,
  },
  // Narrow columns: the same two times, smaller, so the bar keeps its length.
  timeLabelNarrow: {
    fontSize: '11px',
    minWidth: '36px',
  },
  hiddenVideo: {
    // The sphere uses the video element as a WebGL texture source, not as a
    // display surface, so it is hidden with opacity rather than display:none
    // or an off-screen position — iOS Safari refuses to texture a video it
    // considers unrendered.
    //
    // It is sized to fill the container even though nothing sees it. Safari's
    // NATIVE HLS pipeline picks its rendition from the element's rendered
    // size, and at the 1x1px this used to be, it pinned every 360 stream to
    // the bottom rung of the ladder (428x214 observed). VHS ignores element
    // size entirely (limitRenditionByPlayerDimensions: false), so this costs
    // nothing on the browsers that still use it.
    position: 'absolute',
    width: '100%',
    height: '100%',
    left: 0,
    top: 0,
    // Behind the sphere's canvas — the canvas paints over it regardless, but
    // an explicit stacking order keeps a fully transparent video from ever
    // landing on top of the rendered scene.
    zIndex: 0,
    opacity: 0,
    pointerEvents: 'none',
  },
  // iOS Permission Overlay styles
  iosPermissionOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  iosPermissionContent: {
    textAlign: 'center' as const,
    color: 'white',
    padding: '20px',
    maxWidth: '300px',
  },
  iosPermissionTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    marginBottom: '12px',
  },
  iosPermissionText: {
    fontSize: '14px',
    lineHeight: 1.5,
    color: '#ccc',
    marginBottom: '20px',
  },
  iosPermissionButton: {
    backgroundColor: '#007AFF',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    padding: '15px 30px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    width: '100%',
  },
  iosPermissionSkip: {
    backgroundColor: 'transparent',
    color: '#999',
    border: 'none',
    padding: '15px',
    fontSize: '14px',
    cursor: 'pointer',
    marginTop: '10px',
    width: '100%',
  },
  playerWrapperFill: {
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  canvasContainerFill: {
    width: '100%',
    height: '100%',
    position: 'relative',
    backgroundColor: '#000',
    overflow: 'hidden',
    cursor: 'grab',
  },
  // Vertical anchor is set per-render by SubtitleOverlay: it sits just above
  // the control bar while that is visible and slides down near the bottom
  // edge once the UI idle-fades away.
  subtitleContainer: {
    position: 'absolute',
    bottom: '56px',
    left: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'center',
    zIndex: 8,
    pointerEvents: 'none' as const,
    transition: 'bottom 0.2s ease',
  },
  subtitleBox: {
    backgroundColor: 'rgba(0,0,0,0.75)',
    borderRadius: '20px',
    padding: '8px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    maxWidth: '90%',
  },
  subtitleAvatar: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 'bold',
    overflow: 'hidden',
  },
  subtitleText: {
    color: '#fff',
    fontSize: '14px',
    lineHeight: 1.4,
    textAlign: 'center' as const,
  },
  // T18: badges the currently-playing cue as an addendum (added after the
  // video was sealed) rather than part of the sealed base. A base track
  // never renders this — see SubtitleOverlay's addendumBadgeText.
  subtitleTrackBadge: {
    color: 'rgba(255,255,255,0.75)',
    backgroundColor: 'rgba(0,0,0,0.6)',
    borderRadius: '10px',
    padding: '2px 10px',
    pointerEvents: 'none' as const,
  },
  qualityButton: {
    backgroundColor: 'transparent',
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.3)',
    borderRadius: '4px',
    padding: '2px 8px',
    cursor: 'pointer',
    fontFamily: 'monospace',
    fontSize: '12px',
    lineHeight: 1.4,
    whiteSpace: 'nowrap' as const,
  },
  qualityMenu: {
    position: 'absolute' as const,
    bottom: '100%',
    left: '50%',
    transform: 'translateX(-50%)',
    marginBottom: '8px',
    backgroundColor: 'rgba(20,20,20,0.95)',
    borderRadius: '6px',
    padding: '4px 0',
    minWidth: '120px',
    zIndex: 20,
  },
  qualityMenuItem: {
    display: 'block',
    width: '100%',
    backgroundColor: 'transparent',
    color: '#fff',
    border: 'none',
    padding: '8px 16px',
    cursor: 'pointer',
    fontSize: '13px',
    textAlign: 'left' as const,
    whiteSpace: 'nowrap' as const,
  },
  subtitleMenuSectionLabel: {
    padding: '8px 16px 4px',
    fontSize: '11px',
    letterSpacing: '0.06em',
    textTransform: 'uppercase' as const,
    opacity: 0.5,
    color: '#fff',
    whiteSpace: 'nowrap' as const,
  },
  // Separates the three quality words from the ?debug=true rendition ladder
  // below them, so the debug rungs never read as more of the viewer's menu.
  settingsMenuDivider: {
    height: '1px',
    margin: '6px 0',
    backgroundColor: 'rgba(255,255,255,0.15)',
  },
  // The second line under a quality word ("Automatically selects the best
  // quality"). Wraps rather than nowraps, so it can be a sentence.
  settingsMenuHint: {
    padding: '0 16px 8px',
    fontSize: '11px',
    lineHeight: 1.35,
    color: '#fff',
    opacity: 0.55,
  },
  // Hover readout above the scrub bar. The caller positions it by setting
  // `left` to the pointer's offset; the translate keeps it centred on that
  // point, and pointerEvents:none keeps it clear of drag-to-seek.
  scrubTooltip: {
    position: 'absolute' as const,
    bottom: '100%',
    marginBottom: '6px',
    transform: 'translateX(-50%)',
    backgroundColor: 'rgba(20,20,20,0.95)',
    color: '#fff',
    borderRadius: '4px',
    padding: '3px 8px',
    fontFamily: 'monospace',
    fontSize: '12px',
    lineHeight: 1.4,
    whiteSpace: 'nowrap' as const,
    pointerEvents: 'none' as const,
    zIndex: 20,
  },
  subtitleMenuRow: {
    display: 'flex' as const,
    alignItems: 'center' as const,
    justifyContent: 'space-between' as const,
    gap: '16px',
    width: '100%',
    backgroundColor: 'transparent',
    color: '#fff',
    border: 'none',
    padding: '9px 16px',
    cursor: 'pointer',
    fontSize: '13px',
    textAlign: 'left' as const,
    whiteSpace: 'nowrap' as const,
  },
};

export default styles;
